import { useEffect, useState } from 'react';
import { NavLink, Outlet, useParams, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  Archive,
  Shield,
  Bell,
  Bot,
  ChevronDown,
  FileCode,
  History,
  Flame,
  Image as ImageIcon,
  Gamepad2,
  Gauge,
  Gift,
  Hash,
  KeyRound,
  Menu,
  MessageSquare,
  Music2,
  Newspaper,
  PanelLeftClose,
  PanelLeftOpen,
  ScrollText,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  Sparkles,
  Ticket,
  Trophy,
  UserPlus,
  UserMinus,
  Users,
  Volume2,
  X,
  Zap,
} from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/Tooltip';
import { entryDef, useGuildRights, type EntryKey } from '@/lib/access';
import { POSTED_ENTRY } from '@/lib/posted';
import {
  BOT_PLUGINS,
  PLUGINS,
  getDisabledPlugins,
  isHiddenByToggles,
  type PluginKey,
} from '@/lib/plugins';

const COLLAPSED_STORAGE_KEY = 'serverSidebarCollapsed';

function useCollapsedGroups() {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(COLLAPSED_STORAGE_KEY);
      if (!raw) return new Set();
      return new Set(JSON.parse(raw) as string[]);
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify([...collapsed]));
  }, [collapsed]);

  const toggle = (label: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });

  return { collapsed, toggle };
}

const COMPACT_STORAGE_KEY = 'serverSidebarCompact';

/** Icons-only server sidebar (56px), remembered per browser. */
function useCompactSidebar() {
  const [compact, setCompact] = useState(() => {
    try {
      return localStorage.getItem(COMPACT_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggleCompact = () =>
    setCompact((prev) => {
      try {
        localStorage.setItem(COMPACT_STORAGE_KEY, prev ? '0' : '1');
      } catch {}
      return !prev;
    });
  return { compact, toggleCompact };
}

/**
 * ProBot-style server layout: a fixed sub-sidebar on the left lists every
 * per-guild feature, grouped by category. The right side is the routed content
 * area (Outlet). Rendered as a nested Route parent so all /server/:guildId/*
 * children share the sidebar and don't need to re-render the guild header.
 */

interface GuildSettingsResponse {
  settings: {
    guildId: string;
    displayName?: string;
    iconUrl?: string | null;
    prefix: string;
    language: string;
    enabledPlugins: string[];
    moduleConfigs?: Record<string, unknown>;
    /** Plugins the bot would act on (computed in web-api lib/pluginStatus). */
    configuredPlugins?: string[];
  };
}

interface NavItem {
  to: string;
  label: string;
  icon: typeof Bot;
  end?: boolean;
  /**
   * Server switch (Admin-Panel) + access right of the same key: hidden while
   * the switch is off or the user can't at least view it (lib/access.ts).
   * `protection` is a right only — its switches (Anti-Nuke …) show on the page.
   */
  plugin?: PluginKey | 'protection';
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

function buildNav(guildId: string): NavGroup[] {
  const p = (path: string) => `/server/${guildId}${path}`;
  // Order: Übersicht first, then day-to-day (Community + Live), then Werkzeuge,
  // then the config Module group (weniger häufig geöffnet), Verwaltung ganz
  // unten weil Admin-only.
  return [
    {
      label: 'Übersicht',
      items: [{ to: p(''), label: 'Dashboard', icon: Gauge, end: true, plugin: 'dashboard' }],
    },
    {
      label: 'Community',
      items: [
        { to: p('/members'), label: 'Mitglieder', icon: Users, plugin: 'members' },
        { to: p('/tickets'), label: 'Ticket-Liste', icon: Ticket, plugin: 'ticketList' },
        { to: p('/leaderboard'), label: 'XP-Rangliste', icon: Trophy, plugin: 'leaderboard' },
        { to: p('/warnings'), label: 'Warnungen', icon: ShieldAlert, plugin: 'moderation' },
        { to: p('/giveaways'), label: 'Giveaways', icon: Gift, plugin: 'giveaways' },
      ],
    },
    {
      label: 'Live',
      items: [
        { to: p('/voice'), label: 'Voice-Belegung', icon: Volume2, plugin: 'voice' },
        { to: p('/message-rate'), label: 'Nachrichten-Rate', icon: Activity, plugin: 'messageRate' },
      ],
    },
    {
      label: 'Werkzeuge',
      items: [
        { to: p('/actions'), label: 'Aktionen', icon: Zap, plugin: 'actions' },
        { to: p('/embed'), label: 'Embed-Composer', icon: FileCode, plugin: 'embed' },
        { to: p('/media'), label: 'Media-Bibliothek', icon: ImageIcon, plugin: 'media' },
        { to: p('/posts'), label: 'Gepostete Nachrichten', icon: History, plugin: 'posts' },
        { to: p('/music'), label: 'Music', icon: Music2, plugin: 'music' },
        { to: p('/nsfw'), label: 'NSFW-Panel', icon: Flame, plugin: 'nsfw' },
      ],
    },
    {
      label: 'Module',
      items: [
        { to: p('/config/tickets'), label: 'Tickets', icon: Ticket, plugin: 'tickets' },
        { to: p('/config/welcome'), label: 'Welcome', icon: UserPlus, plugin: 'welcome' },
        { to: p('/config/leave'), label: 'Leave', icon: UserMinus, plugin: 'leave' },
        { to: p('/config/leveling'), label: 'Leveling', icon: Sparkles, plugin: 'leveling' },
        { to: p('/config/automod'), label: 'AutoMod', icon: ShieldCheck, plugin: 'autoMod' },
        { to: p('/config/logging'), label: 'Logging', icon: Hash, plugin: 'logging' },
        { to: p('/config/modmail'), label: 'ModMail', icon: MessageSquare, plugin: 'modMail' },
        { to: p('/config/tempchannels'), label: 'Temp Channels', icon: Volume2, plugin: 'tempChannels' },
        { to: p('/config/serverstats'), label: 'Server-Stats', icon: Users, plugin: 'serverStats' },
        { to: p('/config/notifications'), label: 'Notifications', icon: Bell, plugin: 'notifications' },
        { to: p('/config/patch-notes'), label: 'Patch Notes', icon: Newspaper, plugin: 'patchNotes' },
      ],
    },
    {
      label: 'Panels',
      items: [
        { to: p('/config/panels/rules'), label: 'Regeln', icon: ShieldCheck, plugin: 'rules' },
        { to: p('/config/panels/support-rules'), label: 'Support-Regeln', icon: ScrollText, plugin: 'supportRules' },
        { to: p('/config/panels/self-roles'), label: 'Self-Roles', icon: Sparkles, plugin: 'selfRoles' },
        { to: p('/config/games-catalog'), label: 'Games-Katalog', icon: Gamepad2, plugin: 'games' },
      ],
    },
    {
      label: 'Verwaltung',
      items: [
        { to: p('/permabans'), label: 'Permabans', icon: ShieldOff, plugin: 'permabans' },
        { to: p('/command-perms'), label: 'Command-Perms', icon: ShieldCheck, plugin: 'commandPerms' },
        { to: p('/backups'), label: 'Backups', icon: Archive, plugin: 'backups' },
        // Own right; its switches (Anti-Nuke …) are handled on the page itself.
        { to: p('/protection'), label: 'Server-Schutz', icon: Shield, plugin: 'protection' },
        { to: p('/access'), label: 'Zugriff', icon: KeyRound, plugin: 'access' },
      ],
    },
  ];
}

export function ServerLayout() {
  const { guildId } = useParams<{ guildId: string }>();
  const location = useLocation();
  const { data } = useQuery({
    queryKey: ['guildSettings', guildId],
    queryFn: () => api<GuildSettingsResponse>(`/api/guilds/${guildId}/settings`),
    enabled: !!guildId,
  });
  const { can, level, isAdmin } = useGuildRights(guildId);
  const { collapsed, toggle } = useCollapsedGroups();
  const { compact, toggleCompact } = useCompactSidebar();
  // Small screens: the sidebar is an overlay opened from the content header.
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => setMobileOpen(false), [location.pathname]);

  if (!guildId) return null;
  const settings = data?.settings;
  const disabledPlugins = new Set(getDisabledPlugins(settings?.moduleConfigs));
  const configuredPlugins = new Set(settings?.configuredPlugins ?? []);
  const nav = buildNav(guildId);
  const groups = nav
    .map((g) => ({
      ...g,
      items: g.items.filter(
        (it) =>
          (!it.plugin ||
            // No right of its own: visible to whoever may see any of the editors.
            (it.plugin === 'posts'
              ? Object.values(POSTED_ENTRY).some((e) => can(e, 'view'))
              : can(it.plugin as EntryKey, 'view'))) &&
          !(it.plugin && isHiddenByToggles(disabledPlugins, it.plugin))
      ),
    }))
    .filter((g) => g.items.length > 0);
  // Direct navigation to a switched-off item: plugin pages still render with a
  // banner (config can be prepared before re-enabling); dashboard-only pages
  // are replaced by a placeholder.
  const disabledHere = nav
    .flatMap((g) => g.items)
    .find(
      (it) =>
        it.plugin && isHiddenByToggles(disabledPlugins, it.plugin) && location.pathname === it.to
    );
  const disabledDef = disabledHere ? PLUGINS.find((x) => x.key === disabledHere.plugin) : undefined;
  // Ticket-Liste / XP-Rangliste: off because their module is off.
  const blockedBy =
    disabledDef?.requires && !disabledPlugins.has(disabledDef.key)
      ? PLUGINS.find((x) => x.key === disabledDef.requires)
      : undefined;
  // View-only right on a page that has something to edit → read-only mode.
  // Aktionen and Zugriff decide per card / per person themselves, Music too
  // (own playlists stay editable with the view right).
  const currentItem = nav.flatMap((g) => g.items).find((it) => location.pathname === it.to);
  const currentEntry = currentItem?.plugin as EntryKey | undefined;
  const readOnly =
    !!currentEntry &&
    currentEntry !== 'actions' &&
    currentEntry !== 'access' &&
    currentEntry !== 'music' &&
    !!entryDef(currentEntry)?.levels.includes('edit') &&
    level(currentEntry) === 'view';
  const adminLink = isAdmin && (
    <>
      {' '}
      <NavLink to="/admin" className="underline hover:text-foreground">
        Im Admin-Panel aktivieren
      </NavLink>
    </>
  );

  const name = settings?.displayName ?? guildId;
  const activePlugins = BOT_PLUGINS.filter(
    (p) => configuredPlugins.has(p.key) && !disabledPlugins.has(p.key)
  ).length;
  // The mobile overlay always shows the full sidebar.
  const iconsOnly = compact && !mobileOpen;
  const guildIcon = (size: string) =>
    settings?.iconUrl ? (
      <img src={settings.iconUrl} alt="" className={cn(size, 'shrink-0 rounded-full')} />
    ) : (
      <div className={cn(size, 'flex shrink-0 items-center justify-center rounded-full bg-muted')}>
        <Bot className="h-4 w-4 text-muted-foreground" />
      </div>
    );

  return (
    <div className="-m-3 flex h-screen min-h-0 md:-m-8">
      <aside
        className={cn(
          'glass-menu shrink-0 flex-col border-r transition-[width] duration-200',
          iconsOnly ? 'w-14' : 'w-52',
          mobileOpen ? 'fixed inset-y-0 left-16 z-40 flex shadow-2xl md:static md:shadow-none' : 'hidden md:flex'
        )}
      >
        {iconsOnly ? (
          <div className="flex flex-col items-center gap-2 border-b py-3">
            <Tooltip label={name}>{guildIcon('h-8 w-8')}</Tooltip>
            <Tooltip label="Sidebar ausklappen">
              <button
                type="button"
                aria-label="Sidebar ausklappen"
                onClick={toggleCompact}
                className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                <PanelLeftOpen className="h-4 w-4" />
              </button>
            </Tooltip>
          </div>
        ) : (
          <div className="flex items-center gap-2 border-b px-3 py-3">
            {guildIcon('h-8 w-8')}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">{name}</div>
              <div className="truncate text-[10px] text-muted-foreground">{activePlugins} Plugins aktiv</div>
            </div>
            <button
              type="button"
              aria-label="Sidebar einklappen"
              title="Sidebar einklappen"
              onClick={toggleCompact}
              className="hidden h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground md:flex"
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Menü schließen"
              onClick={() => setMobileOpen(false)}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent md:hidden"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        <nav className={cn('scrollbar-thin flex-1 overflow-y-auto overflow-x-hidden', iconsOnly ? 'px-1.5 py-2' : 'px-2 py-3')}>
          {groups.map((group, gi) => {
            const isActive = (it: NavItem) =>
              location.pathname === it.to || (!it.end && location.pathname.startsWith(it.to + '/'));
            const configuredDot = (it: NavItem) =>
              !!it.plugin && it.to.includes('/config/') && configuredPlugins.has(it.plugin);

            if (iconsOnly) {
              return (
                <div key={group.label}>
                  {gi > 0 && <div className="mx-2 my-2 h-px bg-border" />}
                  <ul className="space-y-1">
                    {group.items.map((item) => (
                      <li key={item.to} className="flex justify-center">
                        <Tooltip label={item.label}>
                          <NavLink
                            to={item.to}
                            end={item.end}
                            aria-label={item.label}
                            className={cn(
                              'relative flex h-9 w-9 items-center justify-center rounded-md transition-colors',
                              isActive(item)
                                ? 'bg-primary/15 text-primary'
                                : 'text-foreground/60 hover:bg-accent hover:text-foreground'
                            )}
                          >
                            <item.icon className="h-4 w-4" />
                            {configuredDot(item) && (
                              <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            )}
                          </NavLink>
                        </Tooltip>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            }

            // Auto-expand the group containing the active route so the user
            // never lands on a "hidden" page.
            const groupIsActive = group.items.some(isActive);
            const isCollapsed = collapsed.has(group.label) && !groupIsActive;
            return (
              <div key={group.label} className="mb-3">
                <button
                  type="button"
                  onClick={() => toggle(group.label)}
                  className="mb-1 flex w-full items-center justify-between rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80 hover:text-foreground"
                >
                  <span>{group.label}</span>
                  <ChevronDown className={cn('h-3 w-3 transition-transform', isCollapsed && '-rotate-90')} />
                </button>
                {!isCollapsed && (
                  <ul className="space-y-px">
                    {group.items.map((item) => (
                      <li key={item.to}>
                        <NavLink
                          to={item.to}
                          end={item.end}
                          className={() =>
                            cn(
                              'flex items-center gap-2 rounded-md px-2 py-1 text-[13px] transition-colors max-md:py-2 max-md:text-sm',
                              isActive(item)
                                ? 'bg-primary/15 font-medium text-primary'
                                : 'text-foreground/70 hover:bg-accent hover:text-foreground'
                            )
                          }
                        >
                          <item.icon className="h-4 w-4 shrink-0" />
                          <span className="truncate">{item.label}</span>
                          {configuredDot(item) && (
                            <span className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" title="Konfiguriert" />
                          )}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>
      </aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-30 bg-black/40 md:hidden" onClick={() => setMobileOpen(false)} />
      )}
      <div className="min-w-0 flex-1 overflow-y-auto p-3 md:p-8">
        <div className="mb-4 flex items-center gap-2 md:hidden">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border bg-card px-3 py-2 text-sm"
          >
            <Menu className="h-4 w-4" /> Menü
          </button>
          <span className="truncate text-sm font-semibold">{name}</span>
        </div>
        {disabledHere && disabledDef?.kind === 'plugin' && (
          <div className="mx-auto mb-4 flex max-w-3xl items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide">
              deaktiviert
            </span>
            <span>
              {disabledHere.label} ist auf diesem Server deaktiviert —{' '}
              {disabledDef.needsConfig
                ? 'der Bot ignoriert die Konfiguration.'
                : 'Bot-Commands und Aktionen sind gesperrt.'}
              {adminLink}
            </span>
          </div>
        )}
        {disabledHere && disabledDef?.kind === 'page' ? (
          <div className="mx-auto mt-16 max-w-md rounded-md border bg-card p-6 text-center text-sm">
            <div className="mb-2 font-medium">{disabledHere.label} ist auf diesem Server deaktiviert.</div>
            <div className="text-muted-foreground">
              {blockedBy
                ? `Die Seite hängt am Plugin „${blockedBy.label}", das ausgeschaltet ist.`
                : 'Die Seite wurde im Admin-Panel ausgeblendet.'}
              {adminLink}
            </div>
          </div>
        ) : readOnly ? (
          <>
            <div className="mx-auto mb-4 flex max-w-3xl items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
              <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide">nur ansehen</span>
              Du kannst hier nichts ändern — dafür fehlt dir das Recht „bearbeiten".
            </div>
            {/* Disables every form control below — one switch for all pages. */}
            <fieldset disabled className="m-0 min-w-0 border-0 p-0">
              <Outlet />
            </fieldset>
          </>
        ) : (
          <Outlet />
        )}
      </div>
    </div>
  );
}
